import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { ApiError, api, type PublicConfig, type SignInAccounts } from "../lib/api";
import { buildFullExport, downloadBlob, exportCsv } from "../lib/files";
import { currentSubscription, disablePush, enablePush, pushSupport } from "../lib/push";
import { engine, useContracts, useOnline, useSession } from "../lib/session";
import { setThemeChoice, useThemeChoice, type ThemeChoice } from "../lib/theme";
import { Alert, AlertAction, Badge, Button, Card, CardTitle, Dialog, Field, PageHeader, Select, TextInput, buttonClass, cx, formatBytes } from "../components/ui";
import { GOOGLE_ERRORS } from "./SignIn";
import { formatDate } from "../../shared/dates";
import { CREDIT_PACKS, type CreditPack, type CreditStatus, type CreditWindow } from "../../shared/ai-credits";
import { getCredits } from "../lib/ai";
import { buyPack } from "../lib/credits";
import { getAddress, newAddress, trustSender, untrustSender, type InboxAddress, type TrustedSender } from "../lib/inbox";

export function CreditBar({ label, w, resets }: { label: string; w: CreditWindow; resets: string }) {
  const pct = w.limit ? Math.min(100, Math.round((w.used / w.limit) * 100)) : 100;
  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between gap-3 text-sm">
        <span className="font-medium text-fg">{label}</span>
        <span className="tabular-nums text-muted">
          {w.remaining} of {w.limit} left
        </span>
      </div>
      <div
        className="h-2 overflow-hidden rounded-full bg-subtle"
        role="progressbar"
        aria-label={`${label} used`}
        aria-valuemin={0}
        aria-valuemax={w.limit}
        aria-valuenow={w.used}
      >
        <div className={cx("h-full rounded-full", w.remaining === 0 ? "bg-bad" : pct >= 75 ? "bg-warn" : "bg-accent")} style={{ width: `${pct}%` }} />
      </div>
      <p className="text-xs text-faint">{resets}</p>
    </div>
  );
}

/** AI credits: today's and this month's free ones, bought ones, and buying more. */
export function AiCredits() {
  const online = useOnline();
  const [credits, setCredits] = useState<CreditStatus | null>(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    if (!online) return;
    getCredits().then(setCredits, () => setError(true));
  }, [online]);

  if (!online) return <p className="text-sm text-muted">Connect to the internet to see your AI credits.</p>;
  if (error) return <p className="text-sm text-muted">AI credits aren't available right now.</p>;
  if (!credits) return <p className="text-sm text-faint">Loading…</p>;
  return (
    <div className="space-y-4">
      {credits.earlyUser && (
        <Alert tone="good">
          You're one of our first users, so you get {credits.earlyUser.multiplier}× the free AI credits
          {credits.earlyUser.endsOn ? ` until ${formatDate(credits.earlyUser.endsOn)}` : ""}. Thank you for joining early.
        </Alert>
      )}
      <p className="text-sm text-muted">
        Reading a contract with AI uses about {credits.typicalRead} credits.{" "}
        {credits.readsLeft > 0 ? `You have about ${credits.readsLeft} read${credits.readsLeft === 1 ? "" : "s"} left.` : "You're out of AI reads for now."}
      </p>
      <div className="grid gap-4 sm:grid-cols-2">
        <CreditBar label="Today" w={credits.day} resets="Refills tomorrow." />
        <CreditBar label="This month" w={credits.month} resets={`Refills on ${formatDate(credits.month.resetsOn)}.`} />
      </div>
      {!!credits.paid?.balance && (
        <p className="text-sm text-muted">
          <span className="font-medium text-fg">{credits.paid.balance.toLocaleString("en-IN")} bought credits left.</span> They never expire and are used after your free credits run out.
        </p>
      )}
      <BuyCredits credits={credits} onBought={setCredits} />
      <p className="text-xs text-faint">Documents you've already read with AI don't use credits again.</p>
      <FullScanSetting />
    </div>
  );
}

/** Settings → AI credits → Buy credits: packs paid with Razorpay Checkout, or "coming soon" while the store is off. */
function BuyCredits({ credits, onBought }: { credits: CreditStatus; onBought: (c: CreditStatus) => void }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<{ tone: "good" | "bad"; text: string } | null>(null);
  const first = credits.topUp;
  if (!first.available) {
    return (
      <div id="buy-credits" className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-dashed border-line-strong px-3 py-2.5">
        <p className="text-sm text-muted">
          Need more? Credit packs are coming soon: {first.credits.toLocaleString("en-IN")} credits for ₹{first.priceInr}.
        </p>
        <Button variant="secondary" size="sm" disabled title="Coming soon">
          Buy credits · coming soon
        </Button>
      </div>
    );
  }
  async function buy(pack: CreditPack) {
    setBusy(pack.id);
    setMessage(null);
    try {
      const result = await buyPack(pack);
      if (result.ok) {
        onBought(result.credits);
        setMessage({ tone: "good", text: `🎉 ${pack.credits.toLocaleString("en-IN")} credits added. They never expire and are used after your free credits.` });
      } else if (result.message) setMessage({ tone: "bad", text: result.message });
    } finally {
      setBusy(null);
    }
  }
  return (
    <div id="buy-credits" className="space-y-3 rounded-lg border border-line px-3 py-3">
      <p className="text-sm font-medium text-fg">Buy credits</p>
      <p className="text-sm text-muted">Bought credits never expire. They're used only after your free credits for the day or month run out.</p>
      <div className="grid gap-2 sm:grid-cols-3">
        {CREDIT_PACKS.map((p) => (
          <Button key={p.id} variant="secondary" disabled={!!busy} onClick={() => void buy(p)}>
            {busy === p.id ? "Opening…" : `${p.credits.toLocaleString("en-IN")} credits · ₹${p.priceInr}`}
          </Button>
        ))}
      </div>
      {message && <Alert tone={message.tone}>{message.text}</Alert>}
      <p className="text-xs text-faint">Payments are handled by Razorpay (UPI, cards, netbanking). We never see your card details.</p>
    </div>
  );
}

/** "Always run a full scan": AI reads every page of big documents instead of the most relevant parts. */
function FullScanSetting() {
  const { me, refreshMe } = useSession();
  const online = useOnline();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const on = !!me?.preferences?.aiFullScan;
  return (
    <div className="space-y-1.5 border-t border-line pt-4">
      <label className="flex items-start gap-3">
        <input
          type="checkbox"
          className="mt-1 h-4 w-4 accent-[var(--ck-accent)]"
          checked={on}
          disabled={saving || !online}
          onChange={async (e) => {
            setSaving(true);
            setError(null);
            try {
              await api("/api/me", { method: "PATCH", json: { aiFullScan: e.target.checked } });
              await refreshMe();
            } catch (err) {
              setError(err instanceof Error ? err.message : "Couldn't save.");
            } finally {
              setSaving(false);
            }
          }}
        />
        <span>
          <span className="block text-sm font-medium text-fg">Always run a full scan</span>
          <span className="block text-xs text-faint">
            For big documents, AI normally reads the first pages and the most relevant parts. A full scan reads every page: it finds more, takes longer and uses more
            credits (a 70-page policy is about 200), and it can use this month's credits beyond today's.
          </span>
        </span>
      </label>
      {on && <p className="text-xs font-medium text-accent-fg">Full scan is on: every upload is read in full.</p>}
      {error && <p className="text-xs text-bad">{error}</p>}
    </div>
  );
}

const LINK_ERRORS: Record<string, string> = {
  ...GOOGLE_ERRORS,
  google_mismatch: "That email is already linked to a different Google account.",
  google_link_signed_out: "You were signed out before Google finished. Sign in and try again.",
  merge_shared_workspace: "That account's workspace has other members, so it can't be merged.",
  merge_not_admin: "Only a workspace admin can merge another account into it.",
  account_gone: "That account no longer exists.",
};

const moved = (n: number) => (n ? ` ${n} contract${n === 1 ? "" : "s"} moved in from its old account.` : "");

/** The emails and Google accounts that sign in to this account; adding one merges its own account in. */
/** Settings → Add contracts by email: the workspace's private address. */
function EmailInCard() {
  const online = useOnline();
  const [data, setData] = useState<InboxAddress | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getAddress().then(setData, () => setData({ enabled: false, address: null }));
  }, []);

  async function make() {
    setConfirm(false);
    setBusy(true);
    setError(null);
    try {
      setData(await newAddress());
      setCopied(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't make an address.");
    } finally {
      setBusy(false);
    }
  }

  async function copy() {
    if (!data?.address) return;
    try {
      await navigator.clipboard.writeText(data.address);
      setCopied(true);
    } catch {
      setError("Couldn't copy. Select the address and copy it yourself.");
    }
  }

  if (!data) return null;
  if (!data.enabled) return <p className="text-sm text-muted">Coming soon: forward contracts to your own ClauseKeep address and we'll fill them in for you.</p>;
  return (
    <div className="space-y-3">
      {data.address ? (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <code className="min-w-0 flex-1 break-all rounded-lg border border-line bg-subtle px-3 py-2 text-sm">{data.address}</code>
            <Button variant="secondary" size="sm" onClick={() => void copy()}>
              {copied ? "Copied ✓" : "Copy"}
            </Button>
          </div>
          <p className="text-sm text-muted">
            Save it as a contact, then email or forward PDF and Word contracts to it, from any address. You can give it to your lawyer or landlord too. We read each file, fill in the details and
            reply so you can create the contract in one tap. Anything sent here waits in your <Link to="/inbox" className="font-medium underline underline-offset-2">Inbox</Link> until you save it.
          </p>
          <Button variant="ghost" size="sm" disabled={!online || busy} onClick={() => setConfirm(true)}>
            Make a new address
          </Button>
          <TrustedSenders trusted={data.trusted ?? []} onChange={(trusted) => setData({ ...data, trusted })} />
          <Dialog open={confirm} title="Make a new address?" onClose={() => setConfirm(false)}>
            <p className="text-sm">The current address stops working right away, and emails to it are refused. Do this if it was shared with someone it shouldn't have been.</p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setConfirm(false)}>
                Keep it
              </Button>
              <Button onClick={() => void make()}>Make a new address</Button>
            </div>
          </Dialog>
        </>
      ) : (
        <>
          <p className="text-sm text-muted">Get a private email address for this workspace. Forward contracts to it and they arrive in your Inbox, already filled in.</p>
          <Button disabled={!online || busy} onClick={() => void make()}>
            {busy ? "Making it…" : "Get my address"}
          </Button>
        </>
      )}
      {error && <Alert tone="bad">{error}</Alert>}
    </div>
  );
}

/**
 * People outside the workspace who get the reply email about what they send and approve their own
 * documents. Everyone else's emails go to the address's owner.
 */
function TrustedSenders({ trusted, onChange }: { trusted: TrustedSender[]; onChange: (trusted: TrustedSender[]) => void }) {
  const online = useOnline();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run(fn: () => Promise<{ trusted: TrustedSender[] }>) {
    setBusy(true);
    setError(null);
    try {
      onChange((await fn()).trusted);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : "That didn't work.");
      return false;
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-2 border-t border-line pt-3">
      <h3 className="text-sm font-semibold">Trusted senders</h3>
      <p className="text-sm text-muted">
        When someone outside your workspace sends a document, the email about it comes to you. Trusted senders get it themselves, with the buttons to create the contract. Their
        contracts are still saved in this workspace.
      </p>
      {trusted.length > 0 && (
        <ul className="divide-y divide-line rounded-lg border border-line" aria-label="Trusted senders">
          {trusted.map((t) => (
            <li key={t.email} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
              <span className="min-w-0 break-all">{t.email}</span>
              <Button variant="ghost" size="sm" aria-label={`Stop trusting ${t.email}`} disabled={!online || busy} onClick={() => void run(() => untrustSender(t.email))}>
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}
      <form
        className="flex flex-wrap gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (email.trim()) void run(() => trustSender(email.trim())).then((ok) => ok && setEmail(""));
        }}
      >
        <TextInput type="email" aria-label="Email address to trust" placeholder="lawyer@example.com" value={email} onChange={(e) => setEmail(e.target.value)} className="min-w-0 flex-1" />
        <Button type="submit" variant="secondary" size="sm" disabled={!online || busy || !email.trim()}>
          Trust
        </Button>
      </form>
      {error && <Alert tone="bad">{error}</Alert>}
    </div>
  );
}

function SignInAccountsCard() {
  const { me, refreshMe } = useSession();
  const online = useOnline();
  const [params, setParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [devLink, setDevLink] = useState<string | null>(null);
  const [googleEnabled, setGoogleEnabled] = useState(false);
  const [message, setMessage] = useState<{ tone: "good" | "bad"; text: string } | null>(() => {
    const n = Number(params.get("moved") ?? 0);
    if (params.get("linked") === "google") return { tone: "good", text: `Google account connected.${moved(n)}` };
    if (params.get("linked") === "email") return { tone: "good", text: `Email added.${moved(n)}` };
    const error = params.get("error");
    return error ? { tone: "bad", text: LINK_ERRORS[error] ?? "Connecting that account failed. Please try again." } : null;
  });

  useEffect(() => {
    api<PublicConfig>("/api/config").then((c) => setGoogleEnabled(c.googleEnabled), () => setGoogleEnabled(false));
    if (params.has("linked") || params.has("error")) {
      void refreshMe();
      setParams({}, { replace: true });
    }
    // Only on first render: the query string is a one-time message.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!me) return null;
  const accounts = me.accounts;
  const isAdmin = me.role === "admin";

  async function remove(path: string) {
    setMessage(null);
    try {
      await api<SignInAccounts>(path, { method: "DELETE" });
      await refreshMe();
    } catch (err) {
      setMessage({ tone: "bad", text: err instanceof Error ? err.message : "Couldn't remove it." });
    }
  }

  return (
    <div className="space-y-4">
      <ul className="divide-y divide-line rounded-lg border border-line">
        {accounts.emails.map((e) => (
          <li key={e.email} className="flex items-center gap-3 px-3 py-2.5 text-sm">
            <span className="min-w-0 flex-1 truncate">{e.email}</span>
            {e.primary ? (
              <Badge>Main · gets reminders</Badge>
            ) : (
              <Button variant="ghost" size="sm" disabled={!online} onClick={() => void remove(`/api/account/emails/${encodeURIComponent(e.email)}`)}>
                Remove
              </Button>
            )}
          </li>
        ))}
        {accounts.google.map((g) => (
          <li key={`google:${g.email}`} className="flex items-center gap-3 px-3 py-2.5 text-sm">
            <span className="min-w-0 flex-1 truncate">
              <span className="font-medium">Google</span> · {g.email}
            </span>
            <Button variant="ghost" size="sm" disabled={!online} onClick={() => void remove("/api/account/google")}>
              Disconnect
            </Button>
          </li>
        ))}
      </ul>

      {isAdmin ? (
        <>
          <form
            className="flex flex-col gap-2 sm:flex-row sm:items-end"
            onSubmit={async (ev) => {
              ev.preventDefault();
              if (busy) return;
              setBusy(true);
              setMessage(null);
              setDevLink(null);
              try {
                const r = await api<{ ok: true; devLink?: string }>("/api/account/emails", { method: "POST", json: { email } });
                setMessage({ tone: "good", text: `We sent a confirmation link to ${email.trim()}. Open it to finish adding the email.` });
                setDevLink(r.devLink ?? null);
                setEmail("");
              } catch (err) {
                setMessage({ tone: "bad", text: err instanceof Error ? err.message : "Couldn't send the link." });
              } finally {
                setBusy(false);
              }
            }}
          >
            <div className="flex-1">
              <Field label="Add another email">
                {(id) => <TextInput id={id} type="email" autoComplete="email" placeholder="you@other-company.com" value={email} onChange={(e) => setEmail(e.target.value)} required />}
              </Field>
            </div>
            <Button type="submit" variant="secondary" disabled={busy || !online}>
              {busy ? "Sending…" : "Send confirmation link"}
            </Button>
          </form>
          {googleEnabled && accounts.google.length === 0 && (
            <a className={buttonClass("secondary")} href="/api/auth/google/start?link=1">
              Connect a Google account
            </a>
          )}
          <p className="text-xs text-faint">
            If the email or Google account already has its own ClauseKeep account, its contracts and files move into this workspace and that account
            closes. After that, any of these sign in here.
          </p>
        </>
      ) : (
        <p className="text-sm text-muted">Only a workspace admin can add sign-in accounts.</p>
      )}
      {message && <Alert tone={message.tone}>{message.text}</Alert>}
      {devLink && (
        <p className="text-xs text-faint">
          Dev only: <a href={devLink}>open the confirmation link</a>
        </p>
      )}
    </div>
  );
}

function timeZones(): string[] {
  try {
    return (Intl as unknown as { supportedValuesOf: (k: string) => string[] }).supportedValuesOf("timeZone");
  } catch {
    return ["UTC"];
  }
}

/** Prompt to turn on push, shown on the dashboard until enabled or dismissed. */
export function PushBanner() {
  const { me } = useSession();
  const [state, setState] = useState<"unknown" | "on" | "off">("unknown");
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem("ck-push-dismissed") === "1";
    } catch {
      return false;
    }
  });
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    currentSubscription().then((s) => setState(s ? "on" : "off"), () => setState("off"));
  }, []);
  if (!me?.push.publicKey || dismissed || state !== "off" || pushSupport() !== "supported") return null;
  const dismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem("ck-push-dismissed", "1");
    } catch {
      // ignore
    }
  };
  return (
    <Alert tone="info" onDismiss={dismiss}>
      Get reminder notifications on this device too, not just email.{" "}
      <AlertAction
        onClick={async () => {
          try {
            await enablePush(me.push.publicKey!);
            setState("on");
          } catch (err) {
            setError(err instanceof Error ? err.message : "Couldn't turn on notifications.");
          }
        }}
      >
        Turn on
      </AlertAction>
      {error && <div className="mt-2 font-medium text-bad">{error}</div>}
    </Alert>
  );
}

const THEMES: { value: ThemeChoice; label: string; icon: string }[] = [
  { value: "system", label: "System", icon: "M4 5h16v11H4zM8 20h8M12 16v4" },
  { value: "light", label: "Light", icon: "M12 4V2m0 20v-2m8-8h2M2 12h2m13.7-5.7 1.4-1.4M4.9 19.1l1.4-1.4m0-11.4L4.9 4.9m14.2 14.2-1.4-1.4M16 12a4 4 0 1 1-8 0 4 4 0 0 1 8 0Z" },
  { value: "dark", label: "Dark", icon: "M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5Z" },
];

/** System / Light / Dark, remembered on this device. */
function Appearance() {
  const choice = useThemeChoice();
  return (
    <fieldset className="space-y-3">
      <legend className="sr-only">Theme</legend>
      <div className="grid max-w-md grid-cols-3 gap-2">
        {THEMES.map((t) => (
          <label
            key={t.value}
            className={cx(
              "flex cursor-pointer flex-col items-center gap-1.5 rounded-lg border px-3 py-3 text-sm font-medium transition-colors has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-accent",
              choice === t.value ? "border-accent bg-accent-soft text-accent-soft-fg" : "border-line-strong bg-surface text-muted hover:bg-subtle hover:text-fg",
            )}
          >
            <input type="radio" name="theme" value={t.value} checked={choice === t.value} onChange={() => setThemeChoice(t.value)} className="sr-only" />
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5" aria-hidden>
              <path d={t.icon} />
            </svg>
            {t.label}
          </label>
        ))}
      </div>
      <p className="text-xs text-faint">System follows your device's light or dark setting. Saved on this device only.</p>
    </fieldset>
  );
}

function Notifications() {
  const { me } = useSession();
  const [on, setOn] = useState<boolean | null>(null);
  const [message, setMessage] = useState<{ tone: "good" | "bad"; text: string } | null>(null);
  const support = pushSupport();
  useEffect(() => {
    currentSubscription().then((s) => setOn(!!s), () => setOn(false));
  }, []);

  if (!me?.push.publicKey) return <p className="text-sm text-muted">Push notifications aren't set up on this server yet. You'll still get email reminders.</p>;
  if (support === "needs-install") return <p className="text-sm text-muted">On iPhone and iPad, add ClauseKeep to your home screen (Share → Add to Home Screen), open it from there, then turn on notifications here.</p>;
  if (support === "unsupported") return <p className="text-sm text-muted">This browser doesn't support notifications. You'll still get email reminders.</p>;
  if (support === "denied") return <p className="text-sm text-muted">Notifications are blocked for this site. Allow them in your browser's site settings, then reload.</p>;

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted">Email reminders are always on. Push notifications are {on ? "on" : "off"} for this device.</p>
      <div className="flex flex-wrap gap-2">
        {on ? (
          <>
            <Button
              variant="secondary"
              onClick={async () => {
                try {
                  const r = await api<{ sent: number }>("/api/push/test", { method: "POST" });
                  setMessage({ tone: r.sent ? "good" : "bad", text: r.sent ? "Test notification sent." : "The test didn't arrive. Try turning notifications off and on." });
                } catch (err) {
                  setMessage({ tone: "bad", text: err instanceof Error ? err.message : "Test failed." });
                }
              }}
            >
              Send a test
            </Button>
            <Button variant="ghost" onClick={async () => { await disablePush(); setOn(false); }}>
              Turn off on this device
            </Button>
          </>
        ) : (
          <Button
            onClick={async () => {
              try {
                await enablePush(me.push.publicKey!);
                setOn(true);
                setMessage(null);
              } catch (err) {
                setMessage({ tone: "bad", text: err instanceof Error ? err.message : "Couldn't turn on notifications." });
              }
            }}
          >
            Turn on notifications
          </Button>
        )}
      </div>
      {message && <Alert tone={message.tone}>{message.text}</Alert>}
    </div>
  );
}

export function Settings() {
  const { me, refreshMe, signOut } = useSession();
  const contracts = useContracts();
  const online = useOnline();
  const zones = useMemo(timeZones, []);
  const [name, setName] = useState(me?.user.name ?? "");
  const [orgName, setOrgName] = useState(me?.org.name ?? "");
  const [timezone, setTimezone] = useState(me?.org.timezone ?? "UTC");
  const [saved, setSaved] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState<string | null>(null);

  if (!me) return null;
  const ownerName = (id: string | null) => {
    const m = me.members.find((x) => x.id === id);
    return m ? (m.name ?? m.email) : "";
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Settings"
        actions={
          me.operator && (
            // The top menu has no room for Admin on phones, so operators find it here.
            <Link to="/admin" className={cx(buttonClass("secondary", "sm"), "sm:hidden")}>
              Admin dashboard
            </Link>
          )
        }
      />
      <Card className="space-y-4">
        <CardTitle>Profile and workspace</CardTitle>
        <form
          className="grid gap-4 sm:grid-cols-2"
          onSubmit={async (e) => {
            e.preventDefault();
            setSaved(null);
            setError(null);
            try {
              const isAdmin = me.role === "admin";
              await api("/api/me", { method: "PATCH", json: { name, ...(isAdmin ? { orgName, timezone } : {}) } });
              await refreshMe();
              setSaved("Saved.");
            } catch (err) {
              setError(err instanceof ApiError ? err.message : "Couldn't save.");
            }
          }}
        >
          <Field label="Your name">{(id) => <TextInput id={id} value={name} maxLength={100} onChange={(e) => setName(e.target.value)} />}</Field>
          <Field label="Email">{(id) => <TextInput id={id} value={me.user.email} disabled />}</Field>
          <Field label="Workspace name">{(id) => <TextInput id={id} value={orgName} maxLength={100} onChange={(e) => setOrgName(e.target.value)} disabled={me.role !== "admin"} />}</Field>
          <Field label="Time zone" hint="Reminders arrive after 08:00 in this time zone.">
            {(id, d) => (
              <Select id={id} aria-describedby={d} value={timezone} onChange={(e) => setTimezone(e.target.value)} disabled={me.role !== "admin"}>
                {(zones.includes(timezone) ? zones : [timezone, ...zones]).map((z) => (
                  <option key={z} value={z}>
                    {z}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <div className="sm:col-span-2 flex items-center gap-3">
            <Button type="submit" disabled={!online}>Save</Button>
            {saved && <span className="text-sm font-medium text-good">{saved}</span>}
            {error && <span className="text-sm font-medium text-bad">{error}</span>}
          </div>
        </form>
        <p className="border-t border-line pt-4 text-sm text-faint">
          Storage used: {formatBytes(me.org.storageBytes)} of {formatBytes(me.org.storageQuotaBytes)}.
        </p>
      </Card>

      <Card className="space-y-3">
        <CardTitle description="Emails and Google accounts that sign in to this account. Adding one that has its own account merges it in here.">
          Sign-in accounts
        </CardTitle>
        <SignInAccountsCard />
      </Card>

      <Card className="space-y-3" id="email-in">
        <CardTitle>Add contracts by email</CardTitle>
        <EmailInCard />
      </Card>

      <Card className="space-y-3" id="ai-credits">
        <CardTitle description="AI reads uploaded contracts and fills in the details for you. Free credits refill every day and every month.">AI credits</CardTitle>
        <AiCredits />
      </Card>

      <Card className="space-y-3">
        <CardTitle description="Choose how ClauseKeep looks on this device.">Appearance</CardTitle>
        <Appearance />
      </Card>

      <Card className="space-y-3">
        <CardTitle>Notifications</CardTitle>
        <Notifications />
      </Card>

      <Card className="space-y-3">
        <CardTitle description="Your data is yours. Download a spreadsheet, or everything including files.">Export your data</CardTitle>
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" disabled={!contracts?.length} onClick={() => contracts && exportCsv(contracts, ownerName)}>
            Download CSV
          </Button>
          <Button
            variant="secondary"
            disabled={!contracts?.length || !!exporting || !online}
            onClick={async () => {
              if (!contracts) return;
              setExporting("Preparing…");
              try {
                const zip = await buildFullExport(contracts, ownerName, (done, total) => setExporting(`Downloading files ${done} of ${total}…`));
                downloadBlob(new Blob([zip], { type: "application/zip" }), `clausekeep-export-${new Date().toISOString().slice(0, 10)}.zip`);
              } finally {
                setExporting(null);
              }
            }}
          >
            {exporting ?? "Download everything (ZIP)"}
          </Button>
        </div>
      </Card>

      <Card className="space-y-3">
        <CardTitle>This device</CardTitle>
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" disabled={!online} onClick={() => void engine.resetAndPull()}>
            Refresh from server
          </Button>
          <Button variant="secondary" onClick={() => void signOut(false)}>
            Sign out
          </Button>
          <Button variant="ghost" onClick={() => void signOut(true)} disabled={!online}>
            Sign out on all devices
          </Button>
        </div>
        <p className="text-xs text-faint">Signing out removes the saved contracts and files from this device.</p>
      </Card>
    </div>
  );
}
